import styles from './HomeSeller.module.scss'
import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Paper,
  Skeleton,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Container,
  Divider,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../../../redux/reducers/rootReducer'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ArticleIcon from '@mui/icons-material/Article'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import instance from '../../../../../../../providers/axios.config'
import Notiflix from 'notiflix'
import { typeResponse } from '../../../../../../../utils/types'
import { ROUTES } from '../../../../../../../utils/constants'
import { Title } from '../../Title'
import React from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { formatDate } from '../../../../../../../utils/helpers'

// Types
interface ISellerStats {
  images_declined: number
  images_licensing: number
  images_pending: number
  unpaid_earnings: number
  current_month_earnings?: number
  current_month_downloads?: number
  total_downloads?: number
  total_publications?: number
  total_earnings?: number
  joined: string
}

interface IArticle {
  uuid: string
  title: string
  content: string
  read: number // 0 = unread, 1 = read
  created_at: string
}

interface IMonthlyEarning {
  month: number
  month_name: string
  earnings: number
}

interface IYearlyEarningsResponse {
  year: number
  monthly_earnings: IMonthlyEarning[]
  total_year_earnings: number
  available_years: number[]
}

// Slide transition for fullscreen dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />
})

// Helper functions
const formatCurrency = (value: string | number): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num || 0)
}

const formatNumber = (value: number | string): string => {
  const num = typeof value === 'string' ? parseInt(value) : value
  return new Intl.NumberFormat('en-US').format(num || 0)
}

const formatFullDate = (dateString: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return `${Math.floor(diffDays / 365)} years ago`
}

// Month names for chart
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// News Card Skeleton
const NewsCardSkeleton = () => (
  <div className={styles.newsCardSkeleton}>
    <div className={styles.skeletonImage} />
    <div className={styles.skeletonContent}>
      <div className={styles.skeletonLine} />
      <div className={styles.skeletonLine} />
      <div className={styles.skeletonLine} />
    </div>
  </div>
)

// Chart Skeleton
const ChartSkeleton = () => (
  <div className={styles.chartSkeleton}>
    <div className={styles.chartSkeletonBars}>
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className={styles.chartSkeletonBar}
          style={{ height: `${Math.random() * 60 + 20}%` }}
        />
      ))}
    </div>
  </div>
)

// Custom Tooltip for Chart
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.chartTooltip}>
        <p className={styles.tooltipLabel}>{label}</p>
        <p className={styles.tooltipValue}>
          ${formatCurrency(payload[0].value)}
        </p>
      </div>
    )
  }
  return null
}

export const HomeSeller = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useSelector((state: RootState) => state.user)

  // State
  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState<ISellerStats | null>(null)
  const [articles, setArticles] = useState<IArticle[]>([])
  const [articlesPage, setArticlesPage] = useState(1)
  const [hasMoreArticles, setHasMoreArticles] = useState(true)
  const [loadingArticles, setLoadingArticles] = useState(false)

  // Chart State
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  const [availableYears, setAvailableYears] = useState<number[]>([])
  const [monthlyEarnings, setMonthlyEarnings] = useState<IMonthlyEarning[]>([])
  const [yearTotalEarnings, setYearTotalEarnings] = useState<number>(0)
  const [loadingChart, setLoadingChart] = useState(true)

  // Article Dialog State
  const [selectedArticle, setSelectedArticle] = useState<IArticle | null>(null)
  const [articleDialogOpen, setArticleDialogOpen] = useState(false)

  const getMySellerCards = async () => {
    setIsLoading(true)
    try {
      const res = await instance.get(`seller/cards`) as any
      if (res.data) {
        setStats(res.data)
      }
      setIsLoading(false)
    } catch (error) {
      Notiflix.Report.failure(
        'Error',
        'Failed to fetch data. Please try again later.',
        'OK'
      )
    }
  }

  // Fetch yearly earnings for chart
  const fetchYearlyEarnings = useCallback(async (year: number) => {
    setLoadingChart(true)
    try {
      const response = await instance.get('seller/earnings/yearly', {
        params: { year }
      }) as any
      if (response) {
        // Map the response to chart-friendly format
        const earningsData = response.monthly_earnings || []
        
        // Ensure we have all 12 months with proper data
        const fullYearData: IMonthlyEarning[] = MONTH_NAMES.map((name, index) => {
          const monthData = earningsData.find((m: IMonthlyEarning) => m.month === index + 1)
          return {
            month: index + 1,
            month_name: name,
            earnings: monthData?.earnings || 0
          }
        })

        setMonthlyEarnings(fullYearData)
        setYearTotalEarnings(response.total_year_earnings || 0)
        
        // Set available years if provided
        if (response.available_years?.length > 0) {
          setAvailableYears(response.available_years)
        }
      }
    } catch (error) {
      console.error('Failed to fetch yearly earnings:', error)
      // Set empty data on error
      setMonthlyEarnings(MONTH_NAMES.map((name, index) => ({
        month: index + 1,
        month_name: name,
        earnings: 0
      })))
      setYearTotalEarnings(0)
    } finally {
      setLoadingChart(false)
    }
  }, [])

  // Fetch articles
  const fetchArticles = useCallback(async (page: number, append: boolean = false) => {
    if (loadingArticles) return

    try {
      setLoadingArticles(true)
      const response = await instance.get('articles', {
        params: {
          page,
          per_page: 5
        },
      }) as any
      if (response.data) {
        const { currentPage, totalPages } = response.data.pagination

        if (append) {
          setArticles(prev => [...prev, ...response.data.articles])
        } else {
          setArticles(response.data.articles || [])
        }

        setHasMoreArticles(currentPage < totalPages)
        setArticlesPage(currentPage)
      }
    } catch (error) {
      if (!append) {
        setArticles([])
      }
      setHasMoreArticles(false)
    } finally {
      setLoadingArticles(false)
    }
  }, [loadingArticles])

  // Mark article as read
  const markArticleAsRead = async (articleUuid: string) => {
    try {
      await instance.post(`articles/${articleUuid}/read`)
      // Update local state to mark as read
      setArticles(prev =>
        prev.map(article =>
          article.uuid === articleUuid
            ? { ...article, read: 1 }
            : article
        )
      )
      // Also update selected article if it's the same
      if (selectedArticle?.uuid === articleUuid) {
        setSelectedArticle(prev => prev ? { ...prev, read: 1 } : null)
      }
    } catch (error) {
      console.error('Failed to mark article as read:', error)
    }
  }

  // Delete article
  const deleteArticle = async (articleUuid: string, e?: React.MouseEvent) => {
    // Stop propagation to prevent opening the article dialog
    if (e) {
      e.stopPropagation()
    }

    Notiflix.Confirm.show(
      t('delete_article', 'Delete Article'),
      t('delete_article_confirm', 'Are you sure you want to delete this article?'),
      t('yes', 'Yes'),
      t('no', 'No'),
      async () => {
        try {
          Notiflix.Loading.circle()
          await instance.delete(`articles/${articleUuid}`)
          
          // Remove from local state
          setArticles(prev => prev.filter(article => article.uuid !== articleUuid))
          
          // Close dialog if the deleted article is currently open
          if (selectedArticle?.uuid === articleUuid) {
            handleCloseArticleDialog()
          }

          Notiflix.Loading.remove()
          Notiflix.Notify.success(t('article_deleted', 'Article deleted successfully'))
        } catch (error) {
          Notiflix.Loading.remove()
          Notiflix.Notify.failure(t('article_delete_error', 'Failed to delete article'))
        }
      },
      () => {
        // User clicked "No" - do nothing
      },
      {
        titleColor: '#a0130e',
        okButtonBackground: '#a0130e',
      }
    )
  }

  // Load more articles handler
  const handleLoadMore = () => {
    if (hasMoreArticles && !loadingArticles) {
      fetchArticles(articlesPage + 1, true)
    }
  }

  // Handle year change
  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const year = Number(event.target.value)
    setSelectedYear(year)
    fetchYearlyEarnings(year)
  }

  // Generate available years (current year down to 5 years ago or from API)
  const getYearOptions = (): number[] => {
    if (availableYears.length > 0) {
      return availableYears
    }
    const currentYear = new Date().getFullYear()
    const years: number[] = []
    for (let i = 0; i < 5; i++) {
      years.push(currentYear - i)
    }
    return years
  }

  // Initial data fetch
  useEffect(() => {
    getMySellerCards()
    fetchArticles(1)
    fetchYearlyEarnings(selectedYear)
  }, [])

  // Navigation handlers
  const handleNavigate = (path: string) => {
    navigate(path)
  }

  // Article dialog handlers
  const handleArticleClick = (article: IArticle) => {
    setSelectedArticle(article)
    setArticleDialogOpen(true)
    
    // Mark as read if not already read
    if (article.read === 0) {
      markArticleAsRead(article.uuid)
    }
  }

  const handleCloseArticleDialog = () => {
    setArticleDialogOpen(false)
    setTimeout(() => {
      setSelectedArticle(null)
    }, 300)
  }

  const getTitleAndDescription = () => {
    return {
      title: t('seller_dashboard', 'Seller Dashboard'),
      description: t('seller_dashboard_description', 'Welcome back! Here\'s an overview of your account performance, earnings, and content status.'),
    }
  }

  // Count unread articles
  const unreadCount = articles.filter(a => a.read === 0).length

  // Get current month index for highlighting
  const currentMonth = new Date().getMonth() // 0-indexed

  return (
    <>
      <div className={styles.top}>
        <Title
          title={getTitleAndDescription().title}
          description={getTitleAndDescription().description}
        />
      </div>

      {/* Dashboard Content */}
      <div className={styles.dashboardContent}>
        <div className={styles.statsGrid}>
          {/* Row 1: Joined Date + Unpaid Earnings */}
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>{t('joined', 'Joined')}:</span>
              {isLoading ? (
                <Skeleton width={80} />
              ) : (
                <span className={`${styles.statValue} ${styles.dark}`}>
                  {formatDate(stats?.joined || '')}
                </span>
              )}
            </div>
            <div className={`${styles.statItem} ${styles.highlight}`}>
              <span className={styles.statLabel}>{t('unpaid_earnings', 'UNPAID EARNINGS')}:</span>
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                <span className={`${styles.statValue} ${styles.gold}`}>
                  $ {formatCurrency(stats?.unpaid_earnings || '0')}
                </span>
              )}
            </div>
          </div>

          {/* Row 2: Current Month Stats */}
          <div className={styles.cardsRow}>
            <div onClick={() => handleNavigate(`/${ROUTES.ACCOUNT}/${ROUTES.EARNINGS}`)} className={`${styles.statCard} ${styles.clickable}`}>
              <span className={styles.cardLabel}>{t('current_month_earnings', 'CURRENT MONTH EARNINGS')}:</span>
              {isLoading ? (
                <Skeleton width={80} />
              ) : (
                <span className={styles.cardValue}>
                  $ {formatCurrency(stats?.current_month_earnings || '0')}
                </span>
              )}
            </div>
            <div className={styles.statCard}>
              <span className={styles.cardLabel}>{t('current_month_downloads', 'CURRENT MONTH DOWNLOADS')}:</span>
              {isLoading ? (
                <Skeleton width={60} />
              ) : (
                <span className={`${styles.cardValue} ${styles.dark}`}>
                  {formatNumber(stats?.current_month_downloads || 0)}
                </span>
              )}
            </div>
          </div>

          {/* Row 3: Total Stats */}
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>{t('total_earnings', 'TOTAL EARNINGS')}: $</span>
              {isLoading ? (
                <Skeleton width={80} />
              ) : (
                <span className={`${styles.statValue} ${styles.dark}`}>
                  {formatCurrency(stats?.total_earnings || '0')}
                </span>
              )}
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>{t('total_downloads', 'TOTAL DOWNLOADS')}:</span>
              {isLoading ? (
                <Skeleton width={60} />
              ) : (
                <span className={`${styles.statValue} ${styles.dark}`}>
                  {formatNumber(stats?.total_downloads || 0)}
                </span>
              )}
            </div>
          </div>

          {/* Row 4: Publication Stats */}
          <div className={styles.publicationRow}>
            <div onClick={() => handleNavigate(`/${ROUTES.ACCOUNT}/${ROUTES.PUBLICATIONS}`)} className={`${styles.publicationItem} ${styles.clickable}`}>
              <span className={styles.statLabel}>{t('total_publication', 'Total Publication')}:</span>
              {isLoading ? (
                <Skeleton width={60} />
              ) : (
                <span className={styles.statValue}>
                  {formatNumber(stats?.total_publications || 0)}
                </span>
              )}
            </div>
            <div
              className={`${styles.publicationItem} ${styles.clickable}`}
              onClick={() => handleNavigate(`/${ROUTES.ACCOUNT}/${ROUTES.EARNINGS}/statistics`)}
            >
              <span className={styles.statValue}>{t('top_earnings_per_image', 'Top earnings per image')}</span>
            </div>
          </div>

          {/* Row 5: Status Counts */}
          <div className={styles.statusRow}>
            <div
              className={styles.statusItem}
              onClick={() => handleNavigate(`/${ROUTES.ACCOUNT}/${ROUTES.PUBLICATIONS}/${ROUTES.UNFINISHED}`)}
            >
              <span className={styles.statusLabel}>{t('unfinished', 'Unfinished')}:</span>
              {isLoading ? (
                <Skeleton width={30} />
              ) : (
                <span className={`${styles.statusValue} ${styles.unfinished}`}>
                  {stats?.images_pending || 0}
                </span>
              )}
            </div>
            <div
              className={styles.statusItem}
              onClick={() => handleNavigate(`/${ROUTES.ACCOUNT}/${ROUTES.PUBLICATIONS}/${ROUTES.LICENSING}`)}
            >
              <span className={styles.statusLabel}>{t('licensing', 'Licensing')}:</span>
              {isLoading ? (
                <Skeleton width={30} />
              ) : (
                <span className={`${styles.statusValue} ${styles.licensing}`}>
                  {stats?.images_licensing || 0}
                </span>
              )}
            </div>
            <div
              className={styles.statusItem}
              onClick={() => handleNavigate(`/${ROUTES.ACCOUNT}/${ROUTES.PUBLICATIONS}/${ROUTES.DECLINED}`)}
            >
              <span className={styles.statusLabel}>{t('declined', 'Declined')}:</span>
              {isLoading ? (
                <Skeleton width={30} />
              ) : (
                <span className={`${styles.statusValue} ${styles.declined}`}>
                  {stats?.images_declined || 0}
                </span>
              )}
            </div>
          </div>

          {/* Upload Button */}
          <div className={styles.uploadButtonContainer}>
            <button
              className={styles.uploadButton}
              onClick={() => handleNavigate(`/${ROUTES.ACCOUNT}/upload`)}
            >
              <CloudUploadIcon />
              {t('upload_files', 'UPLOAD FILES')}
            </button>
          </div>
        </div>

        {/* Earnings Chart Section */}
        <div className={styles.earningsChartSection}>
          <div className={styles.chartHeader}>
            <div className={styles.chartTitleArea}>
              <h3>
                <TrendingUpIcon />
                {t('monthly_earnings', 'Monthly Earnings')}
              </h3>
              <span className={styles.chartTotal}>
                {t('year_total', 'Year Total')}: ${formatCurrency(yearTotalEarnings)}
              </span>
            </div>
            <FormControl size="small" className={styles.yearSelector}>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                disabled={loadingChart}
                sx={{
                  minWidth: 100,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#a0130e',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#a0130e',
                  },
                }}
              >
                {getYearOptions().map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className={styles.chartContainer}>
            {loadingChart ? (
              <ChartSkeleton />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={monthlyEarnings}
                  margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="month_name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#666', fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#666', fontSize: 12 }}
                    tickFormatter={(value) => `$${value}`}
                    width={60}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="earnings"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={50}
                  >
                    {monthlyEarnings.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          selectedYear === new Date().getFullYear() && index === currentMonth
                            ? '#a0130e'
                            : entry.earnings > 0
                              ? '#d4a574'
                              : '#e8e8e8'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Chart Legend */}
          <div className={styles.chartLegend}>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ backgroundColor: '#a0130e' }} />
              <span>{t('current_month', 'Current Month')}</span>
            </div>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ backgroundColor: '#d4a574' }} />
              <span>{t('earnings', 'Earnings')}</span>
            </div>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ backgroundColor: '#e8e8e8' }} />
              <span>{t('no_earnings', 'No Earnings')}</span>
            </div>
          </div>
        </div>

        {/* News/Articles Section */}
        <div className={styles.newsSection}>
          <div className={styles.newsSectionHeader}>
            <h3>
              <ArticleIcon />
              {t('news_and_updates', 'News')}
              {unreadCount > 0 && (
                <span className={styles.unreadBadge}>{unreadCount}</span>
              )}
            </h3>
          </div>

          <div className={styles.newsScrollContainer}>
            {articles.length === 0 && !loadingArticles ? (
              <div className={styles.emptyState}>
                <ArticleIcon />
                <p>{t('no_articles', 'No articles available at the moment.')}</p>
              </div>
            ) : (
              <>
                {articles.map((article) => (
                  <div
                    key={article.uuid}
                    className={`${styles.newsCard} ${article.read === 0 ? styles.unread : ''}`}
                    onClick={() => handleArticleClick(article)}
                  >
                    <div className={styles.newsCardContent}>
                      <div className={styles.newsImage}>
                        <div className={styles.newsImagePlaceholder}>
                          <ArticleIcon />
                        </div>
                        {/* Unread indicator */}
                        {article.read === 0 && (
                          <div className={styles.unreadIndicator}>
                            <FiberManualRecordIcon sx={{ fontSize: 10 }} />
                          </div>
                        )}
                      </div>
                      <div className={styles.newsInfo}>
                        <h4 className={styles.newsTitle}>
                          {article.read === 0 && (
                            <span className={styles.newBadge}>{t('new', 'NEW')}</span>
                          )}
                          {article.title}
                        </h4>
                        <div className={styles.newsMeta}>
                          <span>
                            <AccessTimeIcon sx={{ fontSize: 12 }} />
                            {formatRelativeTime(article.created_at)}
                          </span>
                        </div>
                      </div>
                      {/* Delete button */}
                      <div className={styles.newsActions}>
                        <Tooltip title={t('delete', 'Delete')} arrow>
                          <IconButton
                            size="small"
                            className={styles.deleteButton}
                            onClick={(e) => deleteArticle(article.uuid, e)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Loading Skeletons */}
                {loadingArticles && (
                  <>
                    <NewsCardSkeleton />
                    <NewsCardSkeleton />
                  </>
                )}

                {/* Load More Button */}
                {hasMoreArticles && !loadingArticles && (
                  <div className={styles.loadMoreContainer}>
                    <button
                      className={styles.loadMoreButton}
                      onClick={handleLoadMore}
                      disabled={loadingArticles}
                    >
                      {t('load_more', 'Load More')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Article Dialog */}
      <Dialog
        fullScreen
        open={articleDialogOpen}
        onClose={handleCloseArticleDialog}
        TransitionComponent={Transition}
      >
        <AppBar
          sx={{
            position: 'relative',
            backgroundColor: '#a0130e',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseArticleDialog}
              aria-label="close"
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              sx={{
                flex: 1,
                fontWeight: 600,
                fontSize: '1.125rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              component="div"
            >
              {selectedArticle?.title || t('article', 'Article')}
            </Typography>
            <Tooltip title={t('delete', 'Delete')} arrow>
              <IconButton
                color="inherit"
                onClick={() => selectedArticle && deleteArticle(selectedArticle.uuid)}
                aria-label="delete"
                sx={{ mr: 1 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseArticleDialog}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box className={styles.articleDialogContent}>
          <Container maxWidth="md" sx={{ py: 4 }}>
            {selectedArticle && (
              <>
                {/* Article Header */}
                <Box className={styles.articleHeader}>
                  <Typography
                    variant="h4"
                    component="h1"
                    className={styles.articleTitle}
                  >
                    {selectedArticle.title}
                  </Typography>

                  <Box className={styles.articleMeta}>
                    <Box className={styles.articleMetaItem}>
                      <AccessTimeIcon sx={{ fontSize: 18 }} />
                      <span>{formatDate(selectedArticle.created_at)}</span>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Article Content */}
                <Box
                  className={styles.articleBody}
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                />
              </>
            )}
          </Container>
        </Box>
      </Dialog>
    </>
  )
}

export default HomeSeller